import React, { useEffect, useState } from "react";
import { companyStatisticsUseCase } from "../../../../../di/container";
import type {
  OrderAmountSummaryQuery,
  OrderAmountSummaryResponse,
} from "../../../../../domain/types/company/company-statistics";

interface Props {
  companyId: number;
  query: OrderAmountSummaryQuery;
  /** 사용자가 고른 조건을 사람이 읽을 수 있게 적은 것. 예: ["검색어 '대한'", "2026.10.01 ~ 2026.10.31"] */
  conditionLabels: string[];
  onClose: () => void;
}

/** 현재 주문 검색 조건 그대로의 업체별·종목별 주문금액 요약. */
export const OrderAmountSummaryDialog: React.FC<Props> = ({ companyId, query, conditionLabels, onClose }) => {
  const [data, setData] = useState<OrderAmountSummaryResponse | null>(null);
  const [error, setError] = useState("");
  const { status, keyword, dateFrom, dateTo } = query;

  useEffect(() => {
    let disposed = false;
    setData(null);
    setError("");
    companyStatisticsUseCase.getOrderAmountSummary(companyId, { status, keyword, dateFrom, dateTo })
      .then(result => { if (!disposed) setData(result); })
      .catch(reason => { if (!disposed) setError(reason instanceof Error ? reason.message : "통계를 불러오지 못했습니다."); });
    return () => { disposed = true; };
  }, [companyId, status, keyword, dateFrom, dateTo]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[6000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <section
        role="dialog"
        aria-modal="true"
        aria-label="주문금액 통계"
        className="relative w-full max-w-[44rem] max-h-[85vh] flex flex-col bg-white rounded-[12px] shadow-xl"
      >
        <header className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 border-b border-[#eeeeee]">
          <div className="min-w-0">
            <h3 className="text-[16px] font-bold text-[#222222]">업체별 · 종목별 주문금액</h3>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(conditionLabels.length ? conditionLabels : ["전체 주문"]).map(label => (
                <span key={label} className="px-2 py-0.5 rounded-[4px] text-[11px] font-medium bg-[#E8EFFF] text-[#4285F4]">
                  {label}
                </span>
              ))}
            </div>
          </div>
          <button
            type="button"
            aria-label="닫기"
            onClick={onClose}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-[8px] hover:bg-[#f5f5f5]"
          >
            <svg className="w-5 h-5 text-[#555555]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div className="overflow-y-auto px-6 py-5">
          {!data && !error && <p role="status" className="py-10 text-center text-[13px] text-[#888888]">통계를 불러오는 중...</p>}
          {error && <p role="alert" className="rounded-[8px] px-4 py-3 text-[13px] bg-[#EBF3FF] text-[#4285F4] border border-[#4285F4]/20">{error}</p>}
          {data && (
            <div className="flex flex-col gap-5">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-[12px] bg-[#FAFAFA] p-4">
                  <p className="text-[12px] font-medium text-[#555555]">주문 수</p>
                  <p className="mt-1 text-[22px] font-bold text-[#222222] tabular-nums">{data.totalOrders.toLocaleString()}건</p>
                </div>
                <div className="rounded-[12px] bg-[#FAFAFA] p-4">
                  <p className="text-[12px] font-medium text-[#555555]">주문금액 합계</p>
                  <p className="mt-1 text-[22px] font-bold text-[#4285F4] tabular-nums">₩{data.totalAmount.toLocaleString()}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <AmountTable
                  title="업체별"
                  rows={data.customers.map(row => ({ id: String(row.customerId), name: row.customerName, count: row.orderCount, amount: row.amount }))}
                />
                <AmountTable
                  title="종목별"
                  rows={data.categories.map(row => ({ id: String(row.categoryId), name: row.categoryName, count: row.orderCount, amount: row.amount }))}
                />
              </div>
              <p className="text-[12px] text-[#888888]">
                배송비와 결제 전 주문을 포함하고, 장바구니·취소·환불 주문은 빼고 합산합니다. 결제 완료 매출과 다를 수 있습니다.
                {data.legacyDateCount > 0 && ` 접수일이 기록되기 전 주문 ${data.legacyDateCount.toLocaleString()}건은 생성일 기준으로 포함했습니다.`}
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

function AmountTable({ title, rows }: { title: string; rows: Array<{ id: string; name: string; count: number; amount: number }> }) {
  return (
    <div className="min-w-0 border border-[#e0e0e0] rounded-[12px] overflow-hidden">
      <h4 className="px-4 py-2.5 text-[13px] font-bold text-[#222222] bg-[#FAFAFA] border-b border-[#eeeeee]">{title}</h4>
      {rows.length === 0 ? (
        <p className="px-4 py-6 text-center text-[13px] text-[#888888]">해당하는 주문이 없습니다.</p>
      ) : (
        <div className="max-h-64 overflow-y-auto">
          <table className="w-full text-[13px]">
            <thead className="sticky top-0 bg-white">
              <tr className="text-[12px] text-[#888888]">
                <th className="px-4 py-2 text-left font-medium">구분</th>
                <th className="px-2 py-2 text-right font-medium">주문 수</th>
                <th className="px-4 py-2 text-right font-medium">주문금액</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id} className="border-t border-[#eeeeee]">
                  <td className="px-4 py-2 text-[#222222] break-all">{row.name}</td>
                  <td className="px-2 py-2 text-right text-[#555555] tabular-nums">{row.count.toLocaleString()}</td>
                  <td className="px-4 py-2 text-right text-[#222222] tabular-nums">₩{row.amount.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
